/* eslint-disable no-console */
import type { RenderParams } from '@seek/forms-ui';
import {
  Box,
  Button,
  FieldMessage,
  Heading,
  Stack,
  Text,
  Alert,
  useResponsiveValue,
  Actions,
} from 'braid-design-system';
import type { UseField } from '@seek/forms-ui/lib/Field/useField';
import type { Language } from '@seek/forms-ui/lib/types';
import type { FormFragment } from '@seek/cmsu-cms-connect';

import { RenderField } from './RenderField';
import React, { useState } from 'react';
import type { Country } from '@seek/melways-sites';
import ReCAPTCHA from 'react-google-recaptcha';
import type { DynamicFormValues, FormField } from './types';
import * as styles from './Form.css';
import { useCMSUContext } from '../../hooks/useCMSUContext';
import { RichText } from '@seek/cmsu-rich-text';
import { useTranslations } from '@vocab/react';
import translations from './.vocab';

type FormProps = RenderParams<DynamicFormValues>;

export interface ComponentProps extends FormProps {
  useField: UseField<DynamicFormValues, Language>;
  country: Country;
  locale: string;
  wptSalesForceApi: string;
  resetForm: () => void;
  sourceName: string;
  privacyUrl: string;
  form: FormFragment;
  recaptchaKey: string;
}

export const Form = ({
  handleSubmit,
  useField,
  resetForm,
  wptSalesForceApi,
  country,
  sourceName,
  form,
  recaptchaKey,
}: ComponentProps) => {
  const { t } = useTranslations(translations);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState<string>('');
  const [recaptchaValue, setRecaptchaValue] = useState('');

  const { language } = useCMSUContext();

  const responsiveValue = useResponsiveValue();
  const isMobile = responsiveValue({
    mobile: true,
    desktop: false,
    tablet: false,
    wide: false,
  });

  const isTablet = responsiveValue({
    mobile: false,
    desktop: false,
    tablet: true,
    wide: false,
  });

  const gridTemplateColumns = (columns: number) => {
    if (isMobile) {
      return 'repeat(1, 1fr)';
    } else if (isTablet) {
      return 'repeat(2, 1fr)';
    }
    return `repeat(${columns}, 1fr)`;
  };
  const gridColumn = (field: FormField) =>
    field.__typename === 'FormTextarea' ||
    field.__typename === 'FormCheckbox' ||
    field.rowSpan
      ? '1 / -1'
      : 'auto';

  const onSubmit = handleSubmit((formValues: DynamicFormValues) => {
    if (!recaptchaValue) {
      setRecaptchaError('Please verify that you are not a robot');
      return;
    }

    const data = {
      ...formValues,
      siteCountry: country.toUpperCase(),
      sourcePage: sourceName,
      recaptcha: recaptchaValue,
    };

    fetch(`${wptSalesForceApi}/${form.apiEndpoint}`, {
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
      .then((response) => {
        if (response.ok) {
          setIsSubmitted(true);
        } else {
          resetForm();
          setSubscriptionError(true);
        }
      })
      .catch(() => {
        setSubscriptionError(true);
        resetForm();
      });
  });

  return (
    <form onSubmit={onSubmit}>
      {!isSubmitted ? (
        <Box padding="medium">
          <Stack space={'medium'}>
            <Heading align={'center'} level="2">
              {form.title ?? ''}
            </Heading>
            <Heading align={'center'} level="4">
              {form.description ?? ''}
            </Heading>
            <Box
              style={{ gridTemplateColumns: gridTemplateColumns(form.columns) }}
              className={styles.styleGrid}
            >
              {form.fields.map((field, key) => (
                <Box style={{ gridColumn: gridColumn(field) }} key={field.key}>
                  <RenderField field={field} key={key} useField={useField} />{' '}
                </Box>
              ))}
            </Box>
            <ReCAPTCHA
              sitekey={recaptchaKey}
              onChange={(val: any) => {
                setRecaptchaValue(val);
                setRecaptchaError('');
              }}
              hl={language}
            />
            {recaptchaError ? (
              <FieldMessage
                id="recaptcha"
                tone="critical"
                message={recaptchaError}
              />
            ) : null}
            <Text size="xsmall">{form.disclaimerText ?? ''}</Text>
            <Actions>
              <Button type="submit" id="submitBtn">
                {form.ctaText || t('Submit')}
              </Button>
            </Actions>

            {subscriptionError && (
              <Box paddingTop="medium" paddingBottom="medium">
                <Alert tone="critical">
                  <Text>Something Wrong</Text>
                </Alert>
              </Box>
            )}
          </Stack>
        </Box>
      ) : (
        <Box paddingBottom="xxlarge" paddingTop="gutter">
          {form.confirmationMessage && (
            <RichText content={form.confirmationMessage.raw} />
          )}
        </Box>
      )}
    </form>
  );
};
